import api from './apiClient';

/**
 * Opportunity Service - Handles all opportunity-related API calls
 */

// Base URL for opportunity endpoints
const BASE_URL = '/opportunities';

export const opportunityService = {
  // Opportunity listing and details
  async getOpportunities(params = {}) {
    try {
      console.log('🔄 Fetching opportunities with params:', params);
      const response = await api.get(BASE_URL, { params });
      console.log('✅ Opportunities fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching opportunities:', error);
      throw error;
    }
  },

  async getOpportunity(id) {
    try {
      console.log(`🔄 Fetching opportunity ${id}`);
      const response = await api.get(`${BASE_URL}/${id}/`);
      console.log('✅ Opportunity fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching opportunity ${id}:`, error);
      throw error;
    }
  },

  async getFeaturedOpportunities() {
    try {
      console.log('🔄 Fetching featured opportunities');
      const response = await api.get(`${BASE_URL}/featured/`);
      console.log('✅ Featured opportunities fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching featured opportunities:', error);
      throw error;
    }
  },

  // Opportunity management (staff only)
  async createOpportunity(opportunityData) {
    try {
      console.log('🔄 Creating opportunity:', opportunityData);
      const response = await api.post(`${BASE_URL}/create/`, opportunityData);
      console.log('✅ Opportunity created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating opportunity:', error);
      throw error;
    }
  },

  async updateOpportunity(id, opportunityData) {
    try {
      console.log(`🔄 Updating opportunity ${id}:`, opportunityData);
      const response = await api.put(`${BASE_URL}/${id}/update/`, opportunityData);
      console.log('✅ Opportunity updated:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error updating opportunity ${id}:`, error);
      throw error;
    }
  },

  // Applications
  async getMyApplications(params = {}) {
    try {
      console.log('🔄 Fetching my applications with params:', params);
      const response = await api.get(`${BASE_URL}/applications/`, { params });
      console.log('✅ My applications fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching my applications:', error);
      throw error;
    }
  },

  // Apply for opportunity


  async applyForOpportunity(applicationData) {
    try {
      console.log('🔄 Applying for opportunity:', applicationData);
      const response = await api.post(`${BASE_URL}/apply/`, applicationData);
      console.log('✅ Application submitted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error applying for opportunity:', error);
      throw error;
    }
  },

  // Wishlist
  async getMyWishlist(params = {}) {
    try {
      console.log('🔄 Fetching my wishlist with params:', params);
      const response = await api.get(`${BASE_URL}/wishlist/`, { params });
      console.log('✅ My wishlist fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching my wishlist:', error);
      throw error;
    }
  },

  async addToWishlist(wishlistData) {
    try {
      console.log('🔄 Adding to wishlist:', wishlistData);
      const response = await api.post(`${BASE_URL}/wishlist/add/`, wishlistData);
      console.log('✅ Added to wishlist:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error adding to wishlist:', error);
      throw error;
    }
  },

  async removeFromWishlist(opportunityId) {
    try {
      console.log(`🔄 Removing opportunity ${opportunityId} from wishlist`);
      const response = await api.delete(`${BASE_URL}/wishlist/remove/${opportunityId}/`);
      console.log('✅ Removed from wishlist');
      return response.data;
    } catch (error) {
      console.error(`❌ Error removing opportunity ${opportunityId} from wishlist:`, error);
      throw error;
    }
  },

  // Statistics and filters
  async getOpportunityStats() {
    try {
      console.log('🔄 Fetching opportunity statistics');
      const response = await api.get(`${BASE_URL}/stats/`);
      console.log('✅ Opportunity stats fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching opportunity stats:', error);
      throw error;
    }
  },

  async getOpportunityFilters() {
    try {
      console.log('🔄 Fetching opportunity filters');
      const response = await api.get(`${BASE_URL}/filters/`);
      console.log('✅ Opportunity filters fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching opportunity filters:', error);
      throw error;
    }
  },

  // Utility functions
  formatOpportunityType(type) {
    const typeMap = {
      'job': 'Job',
      'training': 'Training Course',
      'internship': 'Internship',
      'apprenticeship': 'Apprenticeship',
      'workshop': 'Workshop',
      'seminar': 'Seminar',
      'conference': 'Conference',
      'volunteer': 'Volunteer Opportunity',
      'other': 'Other'
    };
    return typeMap[type] || type;
  },

  formatApplicationStatus(status) {
    const statusMap = {
      'applied': 'Applied',
      'wishlisted': 'Wishlisted',
      'under_review': 'Under Review',
      'accepted': 'Accepted',
      'rejected': 'Rejected',
      'withdrawn': 'Withdrawn'
    };
    return statusMap[status] || status;
  },

  getStatusColor(status) {
    const colorMap = {
      'applied': '#2563eb',      // blue
      'wishlisted': '#7c3aed',   // purple
      'under_review': '#f59e0b', // amber
      'accepted': '#10b981',     // emerald
      'rejected': '#ef4444',     // red
      'withdrawn': '#6b7280'     // gray
    };
    return colorMap[status] || '#6b7280';
  },

  getTypeIcon(type) {
    const iconMap = {
      'job': '💼',
      'training': '📚',
      'internship': '🎓',
      'apprenticeship': '🔧',
      'workshop': '🛠️',
      'seminar': '🎤',
      'conference': '🏛️',
      'volunteer': '🤝',
      'other': '📋'
    };
    return iconMap[type] || '📋';
  },

  formatDeadline(deadline) {
    const date = new Date(deadline);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'Expired';
    } else if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else if (diffDays <= 7) {
      return `${diffDays} days`;
    } else {
      return date.toLocaleDateString();
    }
  },

  getDeadlineUrgency(deadline) {
    const date = new Date(deadline);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'expired';
    if (diffDays <= 3) return 'urgent';
    if (diffDays <= 7) return 'soon';
    return 'normal';
  },

  // Admin: Get all applications
  async getAllApplications(params = {}) {
    try {
      console.log('🔄 Fetching all applications (admin)');
      const response = await api.get(`${BASE_URL}/applications/all/`, { params });
      console.log('✅ All applications fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching all applications:', error);
      throw error;
    }
  },

  // Admin: Update application status
  async updateApplicationStatus(applicationId, status) {
    try {
      console.log(`🔄 Updating application ${applicationId} status to ${status}`);
      const response = await api.patch(`${BASE_URL}/applications/${applicationId}/`, { status });
      console.log('✅ Application status updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating application status:', error);
      throw error;
    }
  }
};

export default opportunityService;
